import { useCallback, useEffect, useRef } from 'react'
import type {
  ConversionPhase,
  JpegQuality,
  OutputFormat,
  WorkerResult,
} from '../types'
import { ConversionWorkerClient } from '../workers/conversionClient'

export type ProgressCallback = (phase: ConversionPhase) => void
const IDLE_WORKER_TIMEOUT_MS = 15_000

export function useWorker() {
  const clientRef = useRef<ConversionWorkerClient | null>(null)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }, [])

  const terminate = useCallback(() => {
    clearIdleTimer()
    clientRef.current?.terminate()
    clientRef.current = null
  }, [clearIdleTimer])

  const scheduleIdleTermination = useCallback(() => {
    clearIdleTimer()
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null
      clientRef.current?.terminate()
      clientRef.current = null
    }, IDLE_WORKER_TIMEOUT_MS)
  }, [clearIdleTimer])

  const createClient = useCallback((): ConversionWorkerClient => {
    if (!clientRef.current) {
      clientRef.current = new ConversionWorkerClient(
        () => new Worker(new URL('../workers/conversion.worker.ts', import.meta.url), { type: 'module' }),
      )
    }
    return clientRef.current
  }, [])

  useEffect(() => terminate, [terminate])

  const start = useCallback(
    (
      taskId: string,
      inputBuffer: ArrayBuffer,
      mimeType: string,
      outputFormat: OutputFormat,
      jpegQuality: JpegQuality,
      onProgress: ProgressCallback,
    ): Promise<WorkerResult> => {
      clearIdleTimer()
      return createClient().convert({
        id: taskId,
        buffer: inputBuffer,
        mimeType,
        options: { outputFormat, jpegQuality },
      }, onProgress).then((result) => {
        scheduleIdleTermination()
        return result
      })
    },
    [clearIdleTimer, createClient, scheduleIdleTermination],
  )

  const cancel = useCallback((taskId: string) => {
    clientRef.current?.cancel(taskId)
  }, [])

  return { start, cancel, terminate }
}
